import { DateTime } from "luxon";
import { IncludeExcludeFilterCategory } from "../../../components/include-exclude-filter/reducers/includeExcludeFilterReducer";
import {
  getPicklistValue,
  type PicklistEntity,
} from "../../../functions/picklistValue";
import { ProjectTask, Resource } from "../canonical";

/**
 * Kiota DateOnly type structure
 */
interface KiotaDateOnly {
  year: number;
  month: number;
  day: number;
}

/**
 * Type guard to check if a value is a Kiota DateOnly object
 */
function isKiotaDateOnly(value: unknown): value is KiotaDateOnly {
  return (
    typeof value === "object" &&
    value !== null &&
    "year" in value &&
    "month" in value &&
    "day" in value
  );
}

/**
 * Formats a date-only value to M/d/yyyy format WITHOUT timezone conversion.
 * This handles multiple input types that may come from different API clients:
 * - JS Date objects (from NSwag/CX)
 * - ISO date strings (from Kiota/CS)
 * - Kiota DateOnly objects with year/month/day properties
 *
 * IMPORTANT: This function treats all dates as local dates without UTC conversion.
 * Use this for date-only fields (like project start/end dates) where timezone
 * shifting would cause off-by-one day errors.
 */
export function formatDateOnly(
  rawDate: Date | string | KiotaDateOnly | undefined | null,
): string {
  if (!rawDate) return "";

  let dt: DateTime;

  if (rawDate instanceof Date) {
    // JS Date - use UTC components since API dates are typically serialized as UTC midnight
    // This avoids timezone offset causing off-by-one day errors
    dt = DateTime.fromObject({
      year: rawDate.getUTCFullYear(),
      month: rawDate.getUTCMonth() + 1,
      day: rawDate.getUTCDate(),
    });
  } else if (isKiotaDateOnly(rawDate)) {
    // Kiota DateOnly object - use components directly
    dt = DateTime.fromObject({
      year: rawDate.year,
      month: rawDate.month,
      day: rawDate.day,
    });
  } else if (typeof rawDate === "string") {
    // ISO string - parse without timezone conversion
    // Extract just the date portion to avoid UTC interpretation
    const dateMatch = new RegExp(/^(\d{4})-(\d{2})-(\d{2})/).exec(rawDate);
    if (dateMatch) {
      dt = DateTime.fromObject({
        year: Number.parseInt(dateMatch[1], 10),
        month: Number.parseInt(dateMatch[2], 10),
        day: Number.parseInt(dateMatch[3], 10),
      });
    } else {
      dt = DateTime.fromISO(rawDate);
    }
  } else {
    return "";
  }

  if (!dt.isValid) return "";
  return dt.toFormat("M/d/yyyy");
}

/**
 * Converts a date value to milliseconds since epoch for comparison.
 * Handles the same input types as formatDateOnly.
 */
export function dateToMillis(
  rawDate: Date | string | KiotaDateOnly | undefined | null,
): number | null {
  if (!rawDate) return null;

  let dt: DateTime;

  if (rawDate instanceof Date) {
    // JS Date - use UTC components since API dates are typically serialized as UTC midnight
    dt = DateTime.fromObject({
      year: rawDate.getUTCFullYear(),
      month: rawDate.getUTCMonth() + 1,
      day: rawDate.getUTCDate(),
    });
  } else if (isKiotaDateOnly(rawDate)) {
    dt = DateTime.fromObject({
      year: rawDate.year,
      month: rawDate.month,
      day: rawDate.day,
    });
  } else if (typeof rawDate === "string") {
    const dateMatch = new RegExp(/^(\d{4})-(\d{2})-(\d{2})/).exec(rawDate);
    if (dateMatch) {
      dt = DateTime.fromObject({
        year: Number.parseInt(dateMatch[1], 10),
        month: Number.parseInt(dateMatch[2], 10),
        day: Number.parseInt(dateMatch[3], 10),
      });
    } else {
      dt = DateTime.fromISO(rawDate);
    }
  } else {
    return null;
  }

  if (!dt.isValid) return null;
  return dt.toMillis();
}

/**
 * Build assignee options from resource data for filter categories
 */
function buildAssigneeOptions(assigneeResources: Resource[]): Array<{
  item: string;
  itemLabel: string;
  checked: boolean;
  categoryId: string;
}> {
  if (!assigneeResources || assigneeResources.length === 0) {
    return [];
  }
  const mapped = assigneeResources
    .map((r: Resource) => {
      const id = String(r.id ?? "");
      if (!id) return null;
      const first = (r.firstName ?? "").trim();
      const last = (r.lastName ?? "").trim();
      const nameCombo = `${first} ${last}`.trim();
      const label = nameCombo || id;
      return {
        item: id,
        itemLabel: label,
        checked: false,
        categoryId: "assignee",
      };
    })
    .filter(
      (
        o,
      ): o is {
        item: string;
        itemLabel: string;
        checked: boolean;
        categoryId: string;
      } => o !== null,
    );
  mapped.sort((a, b) => a.itemLabel.localeCompare(b.itemLabel));
  return mapped;
}

/**
 * Build filter categories for project task filtering
 */
export function buildProjectCategories(
  assigneeResources: Resource[] | undefined,
  phases: string[],
  statuses: number[],
  picklist: PicklistEntity[] | undefined,
): IncludeExcludeFilterCategory[] {
  const assigneeOptions = buildAssigneeOptions(assigneeResources ?? []);

  const cats: IncludeExcludeFilterCategory[] = [
    {
      id: "phase",
      label: "Phase",
      type: "checkbox",
      options: phases.map((phase) => ({
        item: phase,
        checked: false,
        categoryId: "phase",
      })),
    },
    {
      id: "status",
      label: "Status",
      type: "checkbox",
      options: statuses.map((status) => {
        const label = getPicklistValue(
          picklist ?? [],
          "project",
          "status",
          status === 8 ? 2 : status,
        ).label;
        return {
          item: mapToCoreStatus(label),
          checked: false,
          categoryId: "status",
        };
      }),
    },
    {
      id: "daterange",
      label: "Date Range",
      type: "date-range",
      options: [
        { item: "Date Range", checked: false, categoryId: "daterange" },
      ],
    },
  ];
  if (assigneeOptions.length > 0) {
    cats.push({
      id: "assignee",
      label: "Assignee",
      type: "checkbox",
      options: assigneeOptions,
    });
  }
  return cats;
}

/**
 * Extract unique statuses from all tasks
 */
export const getProjectFilterValues = (allTasks: ProjectTask[]) => {
  const statuses = new Set<number>();

  for (const task of allTasks) {
    statuses.add(Number(task.status));
  }

  return { statuses: Array.from(statuses) };
};

/**
 * Extract unique assignee IDs from all tasks
 */
export function extractAssigneeIds(allTasks: ProjectTask[]): string[] {
  const ids = new Set<string>();

  for (const task of allTasks) {
    const assigneeId = task?.assignedResourceID ?? null;

    if (assigneeId != null && assigneeId.toString() !== "0")
      ids.add(String(assigneeId));
  }

  return Array.from(ids).sort((a, b) => a.localeCompare(b));
}

/**
 * Map picklist status labels to core status labels
 */
export const mapToCoreStatus = (label: string) => {
  switch (label?.toLowerCase()) {
    case "new":
    case "inactive":
    case "backlog":
      return "Not Started";
    case "in progress":
    case "at risk":
      return "In Progress";
    case "complete":
    case "completed":
      return "Completed";
    case "on hold":
      return "On Hold";
    default:
      return label;
  }
};

/**
 * Get all possible picklist labels for a given core status
 */
export const getLabelsForCoreStatus = (coreStatus: string) => {
  switch (coreStatus?.toLowerCase()) {
    case "not started":
      return ["New", "Inactive", "Backlog"];
    case "in progress":
      return ["In Progress", "At Risk"];
    case "completed":
    case "complete":
      return ["Complete", "Completed"];
    case "on hold":
      return ["On Hold"];
    default:
      return [coreStatus];
  }
};
